import { Notification, NotificationButton } from '@appTypes/Notifications';
import useNotifications, { NotificationsContextType, notificationTypes } from '@contexts/useNotifications/useNotifications';
import { Avatar, Button, Center, Group, Indicator, Popover, ScrollArea, Tabs, Text, UnstyledButton, rem } from '@mantine/core';
import { IconBell, IconCheck, IconSettings } from '@tabler/icons-react';
import { stringToLowerCaseDashed } from '@utils/strings';
import { timeAgo } from '@utils/time';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationDispatch } from '@domains/navigation';
import classes from './NotificationsMenu.module.css';
import { useBooleanFlagValue } from '@openfeature/react-sdk';


const NotificationItem = ({ notification, onAction, markAsRead }: { notification: Notification, onAction: ( command: NotificationButton['command']) => void, markAsRead: () => void }) => {
  const { t } = useTranslation( 'general' );

  const getNotificationType = () => {
    if ( notification.color ) {
      return {
        color: notification.color,
        icon: notificationTypes[notification.type]?.icon || notificationTypes.default.icon
      };
    }
    return notificationTypes[notification.type] || notificationTypes.default;
  };

  const notificationType = getNotificationType();

  const hoverStart = useRef<Record<string, number>>({});
  const handleMouseLeave = () => {
    const started = hoverStart.current[notification.id] || 0;
    const elapsed = Date.now() - started;
    if ( !notification.read && elapsed >= 500 ) {
      markAsRead();
    }
    delete hoverStart.current[notification.id];
  };

  return (
    <div
        className={`${classes.notificationItem} ${notification.read ? classes.readNotification : ''}`}
        onMouseEnter={() => { hoverStart.current[notification.id] = Date.now(); }}
        onMouseLeave={handleMouseLeave}
    >
      <Group wrap="nowrap" gap="xs">
        <Indicator disabled={notification.read} size={8} offset={3} position="top-end" color="red" withBorder>
          <Avatar size={32} radius="xl" color={notificationType.color}>
            {notification.image ? notification.image : notificationType.icon}
          </Avatar>
        </Indicator>
        <div className={classes.notificationContent}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" fw={500}>{notification.title}</Text>
            <Text size="xs" c="dimmed">{timeAgo( notification.timestamp, t )}</Text>
          </Group>
          <Text size="xs" lineClamp={2} c="gray.8">{notification.content}</Text>
          {notification.buttons && notification.buttons.length > 0 && (
            <Group gap="xs" wrap="wrap" mt={4}>
              {notification.buttons.map(( button, index ) => (
                <Button
                  key={`${notification.id}-button-${index}`}
                  variant="subtle"
                  size="xs"
                  className={classes.actionButton}
                  onClick={() => onAction( button.command ) }
                >
                  {button.text}
                </Button>
              ))}
            </Group>
          )}
        </div>
      </Group>
    </div>
  );
};

export function NotificationsMenu() {
  const { t } = useTranslation( 'notifications' );
  const isNotificationsSettingsEnabled = useBooleanFlagValue( 'notifications-settings', false );
  const { notifications, markAllAsRead, markAsRead, handleAction, hasUnread } = useNotifications() as NotificationsContextType;
  const navigationDispatch = useNavigationDispatch();
  const buttonRef = useRef<HTMLButtonElement>( null );
  const [ popoverOffset, setPopoverOffset ] = useState({ crossAxis: 0, mainAxis: 0 });
  const [ filteredNotifications, setFilteredNotifications ] = useState<Notification[]>([]);
  const [ isOpen, setIsOpen ] = useState( false );
  const [ filter, setFilter ] = useState<'all' | 'read' | 'unread'>( 'all' );


  useEffect(() => {
    const calculateOffset = () => {
      if ( buttonRef.current ) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const distanceToRightEdge = window.innerWidth - buttonRect.right;
        setPopoverOffset({ crossAxis: distanceToRightEdge, mainAxis: 10 });
      }
    };

    calculateOffset();
    window.addEventListener( 'resize', calculateOffset );

    return () => {
      window.removeEventListener( 'resize', calculateOffset );
    };
  }, []);
  useEffect(() => {
    if ( notifications ) {
      let filtered = notifications;
      if ( filter === 'read' ) {
        filtered = filtered.filter( n => n.read );
      } else if ( filter === 'unread' ) {
        filtered = filtered.filter( n => !n.read );
      }
      setFilteredNotifications( filtered );
    }
  }, [ notifications, filter ]);

  const handleSettingsClick = () => {
    setIsOpen( false );
    navigationDispatch(
      { type: 'navigation/request/navigate', payload: { path: '/notifications/configuration' }},
      { source: 'user' }
    );
  };

  const handleViewAll = () => {
    setIsOpen( false );
    navigationDispatch(
      { type: 'navigation/request/navigate', payload: { path: `/${stringToLowerCaseDashed( 'notifications' )}` }},
      { source: 'user' }
    );
  };

  return (
    <Popover
      position="bottom-end"
      withArrow
      shadow="md"
      width={350}
      withinPortal={true}
      offset={popoverOffset}
      opened={isOpen}
      onChange={setIsOpen}
    >
      <Popover.Target>
        <UnstyledButton ref={buttonRef} style={{ marginTop: `${hasUnread ? '6px' : '0px'}` }} onClick={() => setIsOpen( !isOpen )}>
          <Center>
            {hasUnread ?
            <Indicator processing color="red" size={6}>
              <IconBell className={`${classes.icon} ${isOpen ? classes.activeIcon : ''}`} style={{ width: rem( 20 ), height: rem( 20 ) }} stroke={1.5} />
            </Indicator>
            :
            <IconBell className={`${classes.icon} ${isOpen ? classes.activeIcon : ''}`} style={{ width: rem( 20 ), height: rem( 20 ), marginBottom: '1px' }} stroke={1.5} />
            }
          </Center>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown} p={0}>
        <div className={classes.header}>
          <Group justify="space-between" p="xs" wrap="nowrap">
            <Text size="xs" fw={500}>{t( 'notifications_label' )}</Text>
            <Group gap="xs" wrap="nowrap">
              <Button variant="subtle" size="xs" leftSection={<IconCheck size={14} />} onClick={()=> markAllAsRead()} styles={{ label: { fontWeight: 400 }}}>
                {t( 'mark_all_read' )}
              </Button>
              {isNotificationsSettingsEnabled &&
                <UnstyledButton onClick={handleSettingsClick}>
                  <IconSettings className={classes.settingsIcon} size={16} stroke={1.5} />
                </UnstyledButton>
              }
            </Group>
          </Group>
          <Tabs
            value={filter}
            onChange={( value ) => setFilter( value as 'all' | 'read' | 'unread' )}
            variant="default"
            radius="xs"
             styles={{
            tab: {
              'backgroundColor': 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
              'padding': '2px 6px',
              'height': 24,
              'fontSize': 11,
              'fontWeight': 400,
              '&:hover': {
                backgroundColor: 'var(--mantine-color-gray-0)'
              }
            }
          }}
          >
            <Tabs.List grow>
              <Tabs.Tab value="all">{t( 'filter_all' ) }</Tabs.Tab>
              <Tabs.Tab value="unread">{t( 'filter_unread' )}</Tabs.Tab>
              <Tabs.Tab value="read">{t( 'filter_read' ) }</Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </div>

        <ScrollArea.Autosize mah={400} scrollbarSize={6} offsetScrollbars>
          <div className={classes.notificationsList}>
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map( notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onAction={handleAction}
                  markAsRead={()=> markAsRead( notification.id )}
                />
              ))
            ) : (
              <Text ta="center" py="md" size='xs' c="dimmed">
                {t( 'no_notifications' )}
              </Text>
            )}
          </div>
        </ScrollArea.Autosize>

        <div className={classes.footer}>
          <Button fullWidth variant="subtle" onClick={handleViewAll}>
            <Text size="xs">{t( 'view_all' )}</Text>
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
